/**
 * VehicleRemoveTool.jsx
 *
 * Tool for removing vehicles from an insurance contract.
 * Uses A×B=C pattern: Select insured vehicles × Choose reason = Done list
 *
 * A single "Valider" action soft-deletes the vehicle_insurance rows
 * (sets removed_at). Broker email + pipeline work item were theatre
 * (email API doesn't exist, pipeline payload shape never confirmed) —
 * both removed 2026-04-21. Wire back post-salon when the real courtier
 * service ships.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight } from 'lucide-react';
import api from '../../../api';
import { insuranceModuleAPI } from '../../../api/insurance.js';

// Removal reason options — le libellé se résout au rendu (t), pas ici.
const REMOVAL_REASONS = [
  { id: 'sold', icon: '💰' },
  { id: 'stolen', icon: '🚨' },
  { id: 'destroyed', icon: '💥' },
  { id: 'end_lease', icon: '📄' },
  { id: 'transfer', icon: '🔄' },
];

function VehicleRemoveTool({ contract, onClose, onSuccess, onMessage }) {
  const { t } = useTranslation();
  const [insuredVehicles, setInsuredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState(null);
  const [exitDate, setExitDate] = useState(new Date().toISOString().split('T')[0]);
  const [done, setDone] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadInsuredVehicles = async () => {
      try {
        setLoading(true);
        const viRes = await api.vehicleInsurance.getAll();
        if (viRes.ok && viRes.data) {
          const contractInsurances = viRes.data.filter(
            vi => vi.contract_id === contract.id && !vi.removed_at
          );

          const enriched = await Promise.all(
            contractInsurances.map(async vi => {
              try {
                const vRes = await api.vehicle.getById(vi.vehicle_id);
                const vehicle = vRes.ok ? vRes.data : {};
                return {
                  id: vi.id,
                  vehicle_id: vi.vehicle_id,
                  plate: vehicle.license_plate || 'N/A',
                  model: `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || t('insuranceConfig.vehicleRemove.unknownVehicle'),
                  coverage: vi.coverage_type || 'standard',
                  start_date: vi.assigned_at || vi.start_date,
                };
              } catch {
                return {
                  id: vi.id,
                  vehicle_id: vi.vehicle_id,
                  plate: 'N/A',
                  model: t('insuranceConfig.vehicleRemove.unknownVehicle'),
                  coverage: vi.coverage_type || 'standard',
                };
              }
            })
          );
          setInsuredVehicles(enriched);
        }
      } catch (err) {
        console.error('Error loading insured vehicles:', err);
      } finally {
        setLoading(false);
      }
    };

    if (contract?.id) loadInsuredVehicles();
  }, [contract?.id]);

  const toggleVehicle = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Move selected vehicles to done list
  const handleRemove = () => {
    if (selected.size === 0 || !reason) return;
    const reasonObj = REMOVAL_REASONS.find(r => r.id === reason);
    const toMove = insuredVehicles.filter(v => selected.has(v.id));
    const newDone = toMove.map(v => ({
      ...v,
      reasonId: reason,
      reasonName: t(`insuranceConfig.vehicleRemove.reasons.${reasonObj.id}`),
      exitDate,
    }));
    setDone(prev => [...newDone, ...prev]);
    setInsuredVehicles(prev => prev.filter(v => !selected.has(v.id)));
    setSelected(new Set());
  };

  // Apply all queued removals. Single honest action: soft-delete the VIs.
  // Broker notification + pipeline tracking will return post-salon when a
  // real emailing / work-item service exists — today both would be theatre.
  const handleConfirm = async () => {
    // Auto-fold pending selection if the user clicked Valider without clicking
    // Retirer first (matches InsuranceVehiclePicker.handleFinish pattern).
    let pending = done;
    if (done.length === 0 && canRemove) {
      const reasonObj = REMOVAL_REASONS.find(r => r.id === reason);
      const toMove = insuredVehicles.filter(v => selected.has(v.id));
      pending = toMove.map(v => ({
        ...v,
        reasonId: reason,
        reasonName: t(`insuranceConfig.vehicleRemove.reasons.${reasonObj.id}`),
        exitDate,
      }));
    }
    if (pending.length === 0) return;

    try {
      setSubmitting(true);
      for (const v of pending) {
        await insuranceModuleAPI.removeVehicle(v.id, {
          removedAt: v.exitDate,
          notes: v.reasonId,
        });
      }
      onMessage?.({
        type: 'success',
        text: t('insuranceConfig.vehicleRemove.success', {
          count: pending.length,
          policy: contract?.policy_number || '',
        }).trim(),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error processing removal:', err);
      onMessage?.({ type: 'error', text: t('insuranceConfig.vehicleRemove.error') });
    } finally {
      setSubmitting(false);
    }
  };

  const availableVehicles = insuredVehicles;
  const canRemove = selected.size > 0 && reason;
  const canFinish = done.length > 0 || canRemove;

  const footerHint = useMemo(() => {
    if (selected.size > 0 && reason) {
      return t('insuranceConfig.vehicleRemove.hintReady', { count: selected.size });
    }
    if (selected.size > 0) return t('insuranceConfig.vehicleRemove.hintPickReason');
    if (done.length > 0) {
      return t('insuranceConfig.vehicleRemove.hintPending', { count: done.length });
    }
    return t('insuranceConfig.vehicleRemove.hintSelect');
  }, [selected.size, reason, done.length, t]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[900px] h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-3.5 flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold">{t('insuranceConfig.vehicleRemove.title')}</h1>
            <div className="text-xs opacity-85 mt-0.5">
              {t('insuranceConfig.vehicleRemove.contractSubtitle', {
                name: contract?.name || t('insuranceConfig.vehicleRemove.contractFallback'),
                policy: contract?.policy_number || contract?.id,
              })}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded flex items-center justify-center bg-white/15 hover:bg-white/25 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* 3-Column Grid */}
        <div className="flex-1 grid grid-cols-[1fr_220px_1fr] overflow-hidden min-h-0">
          {/* Column A: Insured vehicles */}
          <div className="flex flex-col overflow-hidden border-r border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                {t('insuranceConfig.vehicleRemove.insuredColumn')}
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                  {availableVehicles.length}
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">{t('insuranceConfig.vehicleRemove.insuredHint')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2.5">
              {loading ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-xl mb-2">⏳</div>
                  <div className="text-sm">{t('insuranceConfig.vehicleRemove.loading')}</div>
                </div>
              ) : availableVehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-xl mb-2 opacity-50">✓</div>
                  <div className="text-sm">{t('insuranceConfig.vehicleRemove.insuredEmpty')}</div>
                </div>
              ) : (
                availableVehicles.map(v => (
                  <div
                    key={v.id}
                    onClick={() => toggleVehicle(v.id)}
                    className={`flex items-center justify-between px-3 py-2.5 mb-1.5 rounded cursor-pointer border-2 transition-all select-none ${
                      selected.has(v.id)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-red-400 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-semibold text-sm text-gray-800">{v.plate}</span>
                    <span className="text-xs text-gray-500">{v.model}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Column B: Reason selection */}
          <div className="flex flex-col overflow-hidden bg-gray-50 border-r border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">{t('insuranceConfig.vehicleRemove.reasonColumn')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-3.5">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  {t('insuranceConfig.vehicleRemove.reasonLabel')}
                </div>
                {REMOVAL_REASONS.map(r => (
                  <div
                    key={r.id}
                    onClick={() => setReason(r.id)}
                    className={`px-3 py-2.5 mb-1.5 rounded cursor-pointer border-2 transition-all ${
                      reason === r.id
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className="font-semibold text-xs text-gray-800 flex items-center gap-1.5">
                      <span className="text-xs">{r.icon}</span>
                      {t(`insuranceConfig.vehicleRemove.reasons.${r.id}`)}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('insuranceConfig.vehicleRemove.exitDate')}</label>
                <input
                  type="date"
                  value={exitDate}
                  onChange={e => setExitDate(e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded text-xs text-gray-800 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                />
              </div>

              <div className="bg-amber-50 border border-amber-300 rounded p-2.5 text-[10px] text-amber-800 leading-relaxed">
                {t('insuranceConfig.vehicleRemove.warning')}
              </div>
            </div>
          </div>

          {/* Column C: Done list */}
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                {t('insuranceConfig.vehicleRemove.doneColumn')}
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    done.length > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {done.length}
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">{t('insuranceConfig.vehicleRemove.doneHint')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2.5">
              {done.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="text-xl mb-2 opacity-50">📋</div>
                  <div className="text-sm leading-relaxed">
                    {t('insuranceConfig.vehicleRemove.doneEmptyLine1')}
                    <br />
                    {t('insuranceConfig.vehicleRemove.doneEmptyLine2')}
                  </div>
                </div>
              ) : (
                done.map((v, idx) => (
                  <div
                    key={`${v.id}-${idx}`}
                    className="flex items-center gap-2.5 px-3 py-2.5 mb-1.5 bg-red-50 border border-red-200 rounded"
                  >
                    <div className="w-4.5 h-4.5 bg-red-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <X size={10} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-gray-800">{v.plate}</div>
                      <div className="text-xs text-red-700">{v.reasonName}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <span className="text-sm text-gray-500">{footerHint}</span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleRemove}
              disabled={!canRemove || submitting}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors flex items-center gap-1 ${
                canRemove && !submitting
                  ? 'bg-red-500 border-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={t('insuranceConfig.vehicleRemove.removeActionTitle')}
            >
              {t('insuranceConfig.vehicleRemove.removeAction', { count: selected.size })} <ChevronRight size={14} />
            </button>
            <button
              onClick={handleConfirm}
              disabled={(!canFinish) || submitting}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                canFinish && !submitting
                  ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? t('insuranceConfig.vehicleRemove.submitting') : t('insuranceConfig.vehicleRemove.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VehicleRemoveTool;
